import { Box } from '@mui/material';
import { ResizeObserver } from '@juggle/resize-observer';
import useDimensions from 'react-cool-dimensions';
import { ContainedText } from '../ContainedText';
import type { CardProps } from '@mui/material/Card';

export type ContainedTextListProps = CardProps & {
  containerDataTestId?: string;
  items: string[];
  spacing: {
    gap: number;
    paddingX: number;
    initialWidth?: number;
  };
};

/**
 * Props for configuring a contained text list component from [MicroLibraryCoreUI](https://dev.azure.com/orcha/MicroLibraryTemplate/_git/MicroLibraryCoreUI)
 *
 * @property {string} containerDataTestId - The test ID for the container element.
 *
 */
export const ContainedTextList = ({ ...props }: ContainedTextListProps) => {
  const { observe, width } = useDimensions({ polyfill: ResizeObserver });
  const { spacing, items, containerDataTestId } = props;

  const getTextWidth = (text: string, fontSize = 12) => {
    if (!text) {
      return;
    }

    const widths = [
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 0, 0, 0, 0, 0, 0.2796875, 0.2765625, 0.3546875, 0.5546875,
      0.5546875, 0.8890625, 0.665625, 0.190625, 0.3328125, 0.3328125, 0.3890625,
      0.5828125, 0.2765625, 0.3328125, 0.2765625, 0.3015625, 0.5546875,
      0.5546875, 0.5546875, 0.5546875, 0.5546875, 0.5546875, 0.5546875,
      0.5546875, 0.5546875, 0.5546875, 0.2765625, 0.2765625, 0.584375,
      0.5828125, 0.584375, 0.5546875, 1.0140625, 0.665625, 0.665625, 0.721875,
      0.721875, 0.665625, 0.609375, 0.7765625, 0.721875, 0.2765625, 0.5,
      0.665625, 0.5546875, 0.8328125, 0.721875, 0.7765625, 0.665625, 0.7765625,
      0.721875, 0.665625, 0.609375, 0.721875, 0.665625, 0.94375, 0.665625,
      0.665625, 0.609375, 0.2765625, 0.3546875, 0.2765625, 0.4765625, 0.5546875,
      0.3328125, 0.5546875, 0.5546875, 0.5, 0.5546875, 0.5546875, 0.2765625,
      0.5546875, 0.5546875, 0.221875, 0.240625, 0.5, 0.221875, 0.8328125,
      0.5546875, 0.5546875, 0.5546875, 0.5546875, 0.3328125, 0.5, 0.2765625,
      0.5546875, 0.5, 0.721875, 0.5, 0.5, 0.5, 0.3546875, 0.259375, 0.353125,
      0.5890625,
    ];
    const avg = 0.5279276315789471;
    return (
      text
        .split('')
        .map((c) =>
          c.charCodeAt(0) < widths.length ? widths[c.charCodeAt(0)] : avg
        )
        .reduce((cur, acc) => acc + cur) * fontSize
    );
  };

  const calculateItemWidth = (text: string) => {
    let total = 0;
    const { gap, paddingX } = spacing;
    const textWidth = getTextWidth(text) ?? 0;
    total += gap;
    total += paddingX;
    total += paddingX;
    total += textWidth;
    return total;
  };

  const calculateLastItemToShow = () => {
    let combinedItemsWidth = 0;
    let lastItemToShow;
    let hasFoundLastItemToShow = false;
    const { initialWidth } = spacing;
    const containerWidth = width === 0 && initialWidth ? initialWidth : width;

    for (let i = 0; !hasFoundLastItemToShow; i += 1) {
      const item = items[i];
      const itemWidth = calculateItemWidth(item);
      combinedItemsWidth += itemWidth;

      if (combinedItemsWidth >= containerWidth && !hasFoundLastItemToShow) {
        hasFoundLastItemToShow = true;
        lastItemToShow = i;

        if (items.length > i) {
          const additionalWidth = calculateItemWidth(`+${items.length - i}`);
          combinedItemsWidth += additionalWidth;
          combinedItemsWidth -= itemWidth;

          if (combinedItemsWidth >= containerWidth) {
            lastItemToShow = i - 1;
          }
        }
      }
    }

    return lastItemToShow;
  };

  const lastItemToShow = calculateLastItemToShow();
  const itemsToShow = lastItemToShow ? items.slice(0, lastItemToShow) : items;
  const additionalCount = items.length - itemsToShow.length;
  const {
    spacing: { gap },
  } = props;

  return (
    <div ref={observe}>
      {items && items.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: `${gap}px`,
          }}
          data-testid={containerDataTestId}
        >
          {itemsToShow.map((containedText: string, index: number) => {
            return (
              <ContainedText key={containedText + index}>
                {containedText}
              </ContainedText>
            );
          })}
          {additionalCount > 0 && (
            <ContainedText>+{additionalCount}</ContainedText>
          )}
        </Box>
      )}
    </div>
  );
};
